import mapStoreModule from "./KBaseMapStore";
import { OverlappingMarkerSpiderfier } from "ts-overlapping-marker-spiderfier";
import { VCol, VRow } from "vuetify/lib";
import { gmapApi } from "vue2-google-maps";

export default {
	name: "KBaseMap",

	provide() {
		return {
			storeModuleName: this.storeModuleName
		};
	},

	props: {
		fitMarkerTypeToBounds: String,
		spiderfy: Boolean,
		storeModuleName: {
			required: true,
			type: String
		}
	},

	data: () => ( {
		mapOptions: {
			gestureHandling: "greedy",
			mapTypeControl: true,
			minZoom: 4,
			rotateControl: false
		},
		mapReportedCenter: {}
	} ),

	computed: {
		google: gmapApi,

		autoFollow: {
			get() {
				return this.$store.state[this.storeModuleName].autoFollow;
			},
			set( follow ) {
				this.$store.dispatch( `${this.storeModuleName}/setAutoFollow`, follow );
			}
		},

		mapCenter: {
			get() {
				return this.$store.state[this.storeModuleName].mapCenter;
			},
			set( center ) {
				this.$store.dispatch( `${this.storeModuleName}/setMapCenter`, center );
			}
		},

		mapZoom: {
			get() {
				return this.$store.state[this.storeModuleName].mapZoom;
			},
			set( zoom ) {
				this.$store.dispatch( `${this.storeModuleName}/setMapZoom`, zoom );
			}
		},

		markers() {
			return this.$store.state[this.storeModuleName].markers;
		},

		markersForBounds() {
			const markers = [];

			if ( this.markers && this.fitMarkerTypeToBounds ) {
				Object.keys( this.markers ).forEach( markerId => {
					if ( this.markers[markerId].markerType === this.fitMarkerTypeToBounds ) {
						markers.push( this.markers[markerId] );
					}
				} );
			}

			return markers;
		}
	},

	methods: {
		registerStoreModule() {
			const store = this.$store;

			if ( !store || !store.state || !store.state[this.storeModuleName] ) {
				store.registerModule( this.storeModuleName, mapStoreModule );
			}
		},

		unregisterStoreModule() {
			const store = this.$store;

			if ( store && store.state && store.state[this.storeModuleName] ) {
				store.unregisterModule( this.storeModuleName );
			}
		},

		fitBoundsToMarkers() {
			const bounds = new this.google.maps.LatLngBounds();

			this.markersForBounds.forEach( marker => bounds.extend( marker.position ) );

			const boundsCenter = bounds.getCenter();

			this.mapCenter = {
				lat: boundsCenter.lat(),
				lng: boundsCenter.lng()
			};

			this.$refs.map.$mapObject.fitBounds( bounds );

			this.mapZoom = this.mapZoom > 19 ? 19 : this.mapZoom - 1;
		},

		syncCenter() {
			this.mapCenter = this.mapReportedCenter;
		},

		updateReportedCenter( center ) {
			this.mapReportedCenter = {
				lat: center.lat(),
				lng: center.lng()
			};
		},

		updateZoom( zoom ) {
			this.mapZoom = zoom;
		}
	},

	created() {
		this.registerStoreModule();
	},

	mounted() {
		this.mapReportedCenter = Object.assign( {}, this.mapCenter );

		this.$refs.map.$mapPromise.then( map => {
			if ( this.spiderfy ) {
				this.$store.dispatch(
					`${this.storeModuleName}/setSpiderfier`,
					new OverlappingMarkerSpiderfier( map, {
						keepSpiderfied: true,
						legWeight: 2,
						markersWontMove: false,
						markersWontHide: false,
						nearbyDistance: 20
					} )
				);
			}

			this.$refs.map.resizePreserveCenter();
		} );

		const unwatch = this.$watch( "markersForBounds.length", length => {
			if ( length ) {
				this.fitBoundsToMarkers();
				unwatch();
			}
		}, { immediate: true } );
	},

	beforeDestroy() {
		this.google.maps.event.clearInstanceListeners( this.$refs.map.$mapObject );
		this.unregisterStoreModule();
	},

	render() {
		return <VRow class="fill-height" noGutters>
			{this.$slots["left-sidebar"]}

			<VCol class="pa-0 fill-height">
				<gmap-map
					center={this.mapCenter}
					class="fill-height"
					on={{
						center_changed: this.updateReportedCenter,
						zoom_changed: this.updateZoom
					}}
					onDragstart={() => this.autoFollow = false}
					onIdle={this.syncCenter}
					options={this.mapOptions}
					ref="map"
					style="width: 100%;"
					zoom={this.mapZoom}
				>
					{Boolean( this.$slots.visible ) && <template slot="visible">{this.$slots.visible}</template>}
					{this.$slots.default}
				</gmap-map>
			</VCol>

			{this.$slots["right-sidebar"]}
		</VRow>;
	}
};
